import classNames from "classnames";
import { ShowHide } from "components/layout";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { FormHelperText } from "./FormHelperText";
import { FormTextInput } from "./FormTextInput";
import 'components/form-fields/FormFields.css';

export function FormDateOfBirthPicker(
    {
        value = null,
        loading = false,
        disabled = false,
        headingText = null,
        dangerHelperText = "",
        helperText = "",
        id,
        onDateChange
    }
) {

    const daysInFebruaryLeapYear = 29;
    const daysInFebruaryNonLeapYear = 28;
    const daysInLongMonth = 31;
    const daysInShortMonth = 30;
    const januaryMonth = 1;
    const februaryMonth = 2;
    const marchMonth = 3;
    const mayMonth = 5;
    const julyMonth = 7;
    const augustMonth = 8;
    const octoberMonth = 10;
    const decemberMonth = 12;
    const monthsWith31Days = [januaryMonth, marchMonth, mayMonth, julyMonth, augustMonth, octoberMonth, decemberMonth];

    const inputType = "number";
	const [day, setDay] = useState(value?.getDate().toString().padStart(2, "0") ?? "");
	const [month, setMonth] = useState(value ? (value.getMonth() + 1).toString().padStart(2, "0") : "");
	const [year, setYear] = useState(value?.getFullYear().toString() ?? "");
    const [isUserInput, setIsUserInput] = useState(false);

	const monthInputRef = useRef(null);
	const yearInputRef = useRef(null);

    useEffect(() => {
        if (!isUserInput && value) {
            setDay(value.getDate().toString().padStart(2, "0"));
            setMonth((value.getMonth() + 1).toString().padStart(2, "0"));
            setYear(value.getFullYear().toString());
        }
    }, [value]);

	useEffect(() => {
		const isDayValid = day.length === 2 || (day !== "0" && day !== "");
		const isMonthValid = month.length === 2 || (month !== "0" && month !== "");
		if (isDayValid && isMonthValid && (year.length === 4)) {
			const parsedDay = parseInt(day, 10);
			const parsedMonth = parseInt(month, 10) - 1;
			const parsedYear = parseInt(year, 10);

			if (!isNaN(parsedDay) && !isNaN(parsedMonth) && !isNaN(parsedYear)) {
				const newDate = new Date(parsedYear, parsedMonth, parsedDay);
				if (!isNaN(newDate.getTime())) {
					onDateChange(newDate);
				}
			}
		} else {
			onDateChange(null);
		}

        if (!isUserInput) {
            setIsUserInput(false);
        }
	}, [day, month, year]);

	const onChangeDay = (event) => {
        setIsUserInput(true);
        const text = event.target.value;
		validateDay(text, month, year);
		if (text.length === 2) {
			monthInputRef.current?.focus();
		}
	};

	const getMaxDaysInMonth = (textMonth, textYear) => {
		let maxDaysInMonth = daysInLongMonth;
		if (textMonth !== "" && textMonth !== "0") {
			const parsedMonth = parseInt(textMonth, 10);
			if (parsedMonth === februaryMonth && (textYear.length === 4)) {
				const parsedYear = parseInt(textYear, 10);
				maxDaysInMonth = parsedYear % 4 === 0 ? daysInFebruaryLeapYear : daysInFebruaryNonLeapYear;
			} else {
                maxDaysInMonth = monthsWith31Days.includes(parsedMonth) ? daysInLongMonth : daysInShortMonth;
			}
		}

		return maxDaysInMonth;
	};

	const validateDay = (textDay, textMonth, textYear) => {
		let parsedDay = parseInt(textDay, 10);
		const maxDaysInMonth = getMaxDaysInMonth(textMonth, textYear);

		if (parsedDay > maxDaysInMonth) {
			textDay = maxDaysInMonth.toString().padStart(2, "0");
			parsedDay = maxDaysInMonth;
		}

		if ((textDay.length !== 2) || (parsedDay >= 1 && parsedDay <= maxDaysInMonth)) {
			setDay(textDay);
		}
	};

	const onChangeMonth = (event) => {
        setIsUserInput(true);
        const firstMonthOfAYear = 1;
        const lastMonthOfAYear = 12;
        const text = event.target.value;
		const parsedMonth = parseInt(text, 10);
		if ((text.length !== 2) || (parsedMonth >= firstMonthOfAYear && parsedMonth <= lastMonthOfAYear)) {
			setMonth(text);
			validateDay(day, text, year);
		}
		if (text.length === 2) {
			yearInputRef.current?.focus();
		}
	};

	const onChangeYear = (event) => {
        setIsUserInput(true);
        const text = event.target.value;
		setYear(text);
		if (text.length === 4) {
			validateDay(day, month, text);
		}
	};

	return (
        <div className="form-field">
            <ShowHide
                evaluator={headingText != null}
                show={(
                    <h4 className={classNames({ "bp6-skeleton": loading })}>{headingText}</h4>
                )}
            />
            <FormHelperText loading={loading}>{helperText}</FormHelperText>
            <div className="inline-items">
                <FormTextInput
                    id={`${id}-day`}
                    value={day}
                    onChange={onChangeDay}
                    maxLength={2}
                    placeholder={"DD"}
                    type={inputType}
                    loading={loading}
                    disabled={disabled}
                />
                <FormTextInput
                    id={`${id}-month`}
                    value={month}
                    onChange={onChangeMonth}
                    maxLength={2}
                    placeholder={"MM"}
                    inputRef={monthInputRef}
                    type={inputType}
                    loading={loading}
                    disabled={disabled}
                />
                <FormTextInput
                    id={`${id}-year`}
                    value={year}
                    onChange={onChangeYear}
                    maxLength={4}
                    placeholder={"YYYY"}
                    inputRef={yearInputRef}
                    type={inputType}
                    loading={loading}
                    disabled={disabled}
                />
            </div>
            <FormHelperText loading={loading} danger>{dangerHelperText}</FormHelperText>
        </div>
    );
}

FormDateOfBirthPicker.propTypes = {
    id: PropTypes.string,
    value: PropTypes.instanceOf(Date),
    onDateChange: PropTypes.func,
    loading: PropTypes.bool,
    disabled: PropTypes.bool,
    headingText: PropTypes.string,
    dangerHelperText: PropTypes.string,
    helperText: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
};